import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { TFunction } from 'i18next';
import { BulkAddProductsToSelectionListResponse } from '../../store/api';
import { SOURCE_SELECTIONLIST } from '../../store/constants';
import { ListSourceType } from '../../../models/ListSourceType';
import {
    CopyBundleFromShoppingCartByBundleIdRequest,
    copyBundleGroupFromCartToListByBundleId
} from './copyBundleGroupFromCartToListByBundleId';
import {
    copyBundleGroupFromListToListByBundleId,
    CopyBundleGroupFromSelectionListByBundleIdRequest
} from './copyBundleGroupFromListToListByBundleId';
import { bulkUpdateToList } from './bulkUpdateToList';

type GetRequestParams = (
    | {
          source: typeof ListSourceType.CART;
      }
    | {
          source: typeof ListSourceType.SELECTION_LIST;
          sourceSelectionListId: string;
      }
) & {
    selectionListId: string;
    bundleId: string;
    performChecks: boolean;
};

export const buildCopyProductsCallback = (
    { selectionListId, bundleId, performChecks, ...sourceParams }: GetRequestParams,
    t: TFunction
): (() => Promise<BulkAddProductsToSelectionListResponse>) => {
    return () => {
        try {
            const request: CopyBundleFromShoppingCartByBundleIdRequest = { bundleId };
            const params = { performChecks };

            if (sourceParams.source === SOURCE_SELECTIONLIST) {
                const requestWithSourceList: CopyBundleGroupFromSelectionListByBundleIdRequest = {
                    ...request,
                    sourceSelectionListId: sourceParams.sourceSelectionListId
                };
                return copyBundleGroupFromListToListByBundleId({
                    selectionListId,
                    body: requestWithSourceList,
                    params
                });
            }

            return copyBundleGroupFromCartToListByBundleId({ selectionListId, body: request, params });
        } catch (e) {
            throw new Error(t('notifications.buildAddProductsCallback.error'));
        }
    };
};

type Props =
    | {
          source: typeof ListSourceType.CART;
      }
    | {
          source: typeof ListSourceType.SELECTION_LIST;
          sourceSelectionListId: string;
      };

export const useCopyBundleGroupToSelectionList = (sourceParams: Props) => {
    const { t } = useTranslation();
    const [isLoading, setLoading] = useState(false);

    return {
        isLoading,
        copyToListBulk: (bundleId: string, listId: string, performChecks: boolean) => {
            const apiCallback = buildCopyProductsCallback(
                {
                    selectionListId: listId,
                    bundleId,
                    performChecks,
                    ...sourceParams
                },
                t
            );

            return bulkUpdateToList({
                setLoading,
                notificationMessage:
                    sourceParams.source === ListSourceType.SELECTION_LIST
                        ? t('notifications.copyProductsToList.message')
                        : t('notifications.copyCartToList.message'),
                apiCallback,
                t,
                onSuccess: () => {}
            });
        }
    };
};
